import { useApolloClient } from '@apollo/client';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { graphql } from '../generated/gql';

const GET_PROJECT = graphql(`
  query ProjectProject($projectKey: String) {
    project(projectKey: $projectKey) {
      id
      projectKey
      name
      referenceNumber
      manageBlasts
      storageMode
      customer {
        id
        name
      }
    }
  }
`);

export interface ProjectCustomerProps {
  id?: number | null;
  name?: string | null;
}

export interface ProjectProps {
  id: number;
  projectKey: string;
  referenceNumber: string;
  name: string;
  manageBlasts?: boolean | null;
  storageMode: 'LEGACY' | 'MODERN';
  customer?: ProjectCustomerProps | null;
  // TODO: add companyKey and use that instead of activeCompanyKey in most places
}

export interface ProjectContextProps {
  project?: ProjectProps;
  isLoading: boolean;
  error?: unknown;
  loadProject: (projectKey: string) => void;
  clearProject: () => void;
}

const ProjectContext = createContext<ProjectContextProps>({
  isLoading: false,
  error: new Error('No ProjectContext provider found'),
  loadProject: () => {},
  clearProject: () => {},
});
ProjectContext.displayName = 'ProjectContext';

export const useProject = () => {
  const context = useContext(ProjectContext);

  if (context.project == null) {
    throw new Error('Project not loaded');
  }

  return context.project;
};

export const ProjectProvider: React.FC = ({ children }) => {
  const client = useApolloClient();

  const [project, setProject] = useState<ProjectProps | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>();

  const loadingRef = useRef<string | null>(null);

  const clearProject = useCallback(() => {
    loadingRef.current = null;
    setProject(undefined);
  }, []);

  const loadProject = useCallback<ProjectContextProps['loadProject']>(
    async (projectKey) => {
      if (!projectKey) {
        clearProject();
        return;
      }

      // Abort load if it's already started
      if (loadingRef.current === projectKey) {
        return;
      }

      loadingRef.current = projectKey;

      setProject(undefined);
      setIsLoading(true);
      setError(undefined);

      try {
        const result = await client.query({
          query: GET_PROJECT,
          variables: { projectKey: projectKey },
        });

        // Abort load if we started loading another project
        if (loadingRef.current !== projectKey) {
          return;
        }

        const nextProject = result.data.project;

        if (nextProject == null) {
          return;
        }

        setProject({
          ...nextProject,
          referenceNumber: nextProject.referenceNumber ?? 'N/A',
          storageMode: nextProject.storageMode,
        });
        setIsLoading(false);

        return nextProject;
      } catch (error) {
        setError(error);
        setIsLoading(false);
      }
    },
    [clearProject, client]
  );

  const value = useMemo(
    () => ({
      project,
      isLoading,
      error,
      loadProject,
      clearProject,
    }),
    [clearProject, error, isLoading, loadProject, project]
  ) satisfies ProjectContextProps;

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
};

export default ProjectContext;
